import { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { SpotImage } from "@/common/model/spotimages/SpotImage";
import SortableImageCard from "./SortableImageCard";
import ImageLightbox from "./ImageLightbox";
import ConfirmDialog from "@/components/common/ConfirmDialog";

interface ImageGalleryProps {
  images: SpotImage[];
  coverImage: string | null;
  onReorder: (orderedFilenames: string[]) => Promise<void>;
  onSetCover: (filename: string) => Promise<void>;
  onRemoveFromSelection: (filename: string) => Promise<void>;
  onDelete: (filename: string) => Promise<void>;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  coverImage,
  onReorder,
  onSetCover,
  onRemoveFromSelection,
  onDelete,
}) => {
  const [localImages, setLocalImages] = useState(images);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<SpotImage | null>(null);

  // Update local state when images prop changes
  if (JSON.stringify(images.map(i => i.filename)) !== JSON.stringify(localImages.map(i => i.filename))) {
    setLocalImages(images);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localImages.findIndex((img) => img.filename === active.id);
      const newIndex = localImages.findIndex((img) => img.filename === over.id);

      const newOrder = arrayMove(localImages, oldIndex, newIndex);
      setLocalImages(newOrder);

      try {
        await onReorder(newOrder.map((img) => img.filename));
      } catch (error) {
        // Revert on error
        setLocalImages(images);
      }
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;

    setIsDeleting(true);
    try {
      await onDelete(pendingDelete);
      // Close lightbox if the deleted image was being viewed
      if (lightboxImage?.filename === pendingDelete) {
        setLightboxImage(null);
      }
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  };

  const handleImageClick = (image: SpotImage) => {
    setLightboxImage(image);
  };

  const handleLightboxSetCover = async (filename: string) => {
    await onSetCover(filename);
    setLightboxImage(null);
  };

  const handleLightboxRemove = async (filename: string) => {
    await onRemoveFromSelection(filename);
    setLightboxImage(null);
  };

  const handleLightboxDelete = (filename: string) => {
    setPendingDelete(filename);
  };

  if (localImages.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <p className="text-gray-500">No images selected for this spot.</p>
        <p className="text-gray-400 text-sm mt-1">
          Add images from the available images section below.
        </p>
      </div>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={localImages.map((img) => img.filename)}
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {localImages.map((image) => (
              <SortableImageCard
                key={image.filename}
                image={image}
                isCover={image.filename === coverImage}
                onSetCover={onSetCover}
                onRemove={onRemoveFromSelection}
                onDelete={(filename) => setPendingDelete(filename)}
                onClick={() => handleImageClick(image)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Lightbox */}
      <ImageLightbox
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
        image={lightboxImage}
        images={localImages}
        isCover={lightboxImage?.filename === coverImage}
        onSetCover={handleLightboxSetCover}
        onRemoveFromSelection={handleLightboxRemove}
        onDelete={handleLightboxDelete}
        onNavigate={setLightboxImage}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Delete Image Permanently"
        message="Are you sure you want to permanently delete this image? This will remove the file from storage and cannot be undone."
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        type="danger"
      />
    </>
  );
};

export default ImageGallery;
